import type { TFunction } from "i18next";
import type { TSelector } from "../../../../i18n/types";

// Resolve a single value: lazy i18n selectors → string via the passed-in `t`;
// plain strings pass through unchanged.
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
const res = (t: TFunction<any, any>, v: string | TSelector) => (typeof v === "function" ? t(v) : v);

/**
 * Resolve the user-facing fields of a filter definition (`name`,
 * `selectOptions[].label`, `selectOptions[].description`) from lazy i18n
 * selectors to plain strings, using a `t` whose owning namespace is bound by
 * the caller. Returns the same shape with those fields flattened to strings so
 * generic consumers (e.g. `FilterWidget`) never have to resolve selectors from
 * a namespace they don't bind. Pure function — not a hook.
 */
export function resolveFilterOption<T extends { name?: unknown; selectOptions?: unknown[] }>(
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  t: TFunction<any, any>,
  opt: T,
) {
  return {
    ...opt,
    ...(opt.name != null && { name: res(t, opt.name as string | TSelector) }),
    ...(opt.selectOptions && {
      selectOptions: opt.selectOptions.map((option) => {
        const o = option as { label?: string | TSelector; description?: string | TSelector };
        return {
          ...o,
          ...(o.label != null && { label: res(t, o.label) }),
          ...(o.description != null && { description: res(t, o.description) }),
        };
      }),
    }),
  };
}
